import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Stethoscope, CheckCircle, Heart, Users, ArrowRight, Baby, Brain, Droplets, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

export default function HealthPage() {
  return (
    <div data-testid="health-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E08B7A] via-[#E99A8A] to-[#F0A99A]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/20 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/30 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Stethoscope className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
              Health & Well-being
            </h1>
            <p className="text-xl text-white/90">
              Improving access to healthcare and promoting community wellness
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Images */}
      <section className="relative py-12 overflow-hidden bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="OPAD Health - Community Healthcare"
                className="w-full h-72 object-cover rounded-3xl shadow-2xl"
              />
              <p className="text-center text-slate-600 mt-4 italic">Community health outreach programmes</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="OPAD Health - Maternal Care"
                className="w-full h-72 object-cover rounded-3xl shadow-2xl"
              />
              <p className="text-center text-slate-600 mt-4 italic">Maternal and child health support</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[450px] bg-[#E08B7A]/10 rounded-br-[250px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[350px] bg-[#7BC4E2]/10 rounded-tl-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/90 backdrop-blur-sm p-8 md:p-12 rounded-3xl shadow-lg border border-slate-100">
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                At the <strong className="text-[#4A7C59]">Organization for Poverty Alleviation and Development (OPAD)</strong>, our Health and Well-Being programmes are designed to improve access to essential healthcare services, promote preventive health practices, and support the overall well-being of individuals in vulnerable communities.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Recognizing that good health is fundamental to sustainable development and poverty reduction, OPAD works collaboratively with local partners and health institutions to implement <strong className="text-[#E08B7A]">community-based initiatives</strong> that strengthen healthcare systems and improve health outcomes.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg mb-6">
                Our programmes focus on raising awareness about public health issues, promoting <strong className="text-[#7BC4E2]">hygiene and sanitation practices</strong>, and supporting access to basic healthcare services for underserved populations. We also support mental health awareness initiatives, maternal and child health programmes, and health education campaigns that encourage healthy lifestyles and early disease prevention.
              </p>
              
              <p className="text-slate-700 leading-relaxed text-lg">
                Through partnerships with healthcare providers and community stakeholders, OPAD promotes inclusive health interventions that enhance resilience, improve access to care, and reduce health inequalities. Our work contributes to strengthening community capacity to address health challenges while advancing sustainable development goals related to health, well-being, and social inclusion for present and future generations.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="relative section-padding overflow-hidden bg-[#FDF8F4]">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[450px] h-[400px] bg-[#E08B7A]/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[300px] bg-[#4A7C59]/10 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">What We Do</h2>
            <div className="w-24 h-1 bg-[#E08B7A] mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#E08B7A]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 bg-[#E08B7A]/10 rounded-xl flex items-center justify-center mb-4">
                <ShieldCheck className="w-7 h-7 text-[#E08B7A]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Community Health Awareness</h3>
              <p className="text-slate-600">Promoting hygiene, sanitation, and preventive healthcare practices.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#7BC4E2]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 bg-[#7BC4E2]/10 rounded-xl flex items-center justify-center mb-4">
                <Baby className="w-7 h-7 text-[#7BC4E2]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Maternal & Child Health Support</h3>
              <p className="text-slate-600">Supporting essential healthcare services for mothers and children.</p>
            </motion.div>
            
            <motion.div
              className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg border-t-4 border-[#4A7C59]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-14 h-14 bg-[#4A7C59]/10 rounded-xl flex items-center justify-center mb-4">
                <Brain className="w-7 h-7 text-[#4A7C59]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Mental Health & Well-Being Initiatives</h3>
              <p className="text-slate-600">Encouraging mental health awareness and support programmes.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategic Focus Areas */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[450px] h-[400px] bg-[#7BC4E2]/10 rounded-br-[200px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#E08B7A]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Strategic Focus Areas
              </h2>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Access to Primary Healthcare</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#7BC4E2] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Public Health Education</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Maternal & Child Health</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E5B75F] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Mental Health Support</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#E08B7A] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">Hygiene & Sanitation Promotion</span>
                </li>
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1584515933487-779824d29309?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Healthcare Support"
                className="rounded-3xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E08B7A] via-[#E99A8A] to-[#F0A99A]">
          <div className="absolute top-0 right-0 w-[350px] h-[300px] bg-white/10 rounded-bl-[150px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[120px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-6">
              Need Help? Get Involved Today
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Support our health initiatives and help improve community wellness worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-white hover:bg-slate-100 text-[#E08B7A] rounded-full px-8 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/get-involved">
                <Button className="bg-[#4A7C59] hover:bg-[#3A6B49] text-white rounded-full px-8 py-6 text-lg font-semibold">
                  <Users className="w-5 h-5 mr-2" />
                  Volunteer With Us
                </Button>
              </Link>
              <Link to="/programs">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#E08B7A] rounded-full px-8 py-6 text-lg font-semibold">
                  View All Programs
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
